import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { ArrowRight, CheckCircle, Clock, MapPin, Plus, RefreshCw, Save, Trash2, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Shell = styled.div`
  display: grid;
  gap: 1rem;
`;

const Card = styled.section`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 1.15rem;
  }

  p {
    margin: 0.25rem 0 0;
    color: #64748b;
    font-size: 0.9rem;
  }
`;

const Body = styled.div`
  padding: 1.5rem;
  display: grid;
  gap: 1.25rem;
`;

const RouteCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
  overflow: hidden;
`;

const RouteCardHeader = styled.div`
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  h4 {
    margin: 0;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const RouteCardBody = styled.div`
  padding: 1rem;
  display: grid;
  gap: 1rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.4rem;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  input,
  select,
  textarea {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    padding: 0.7rem 0.8rem;
    color: #0f172a;
    background: #f8fafc;
    font-size: 0.95rem;
    font-weight: 600;
    outline: none;
  }

  textarea {
    min-height: 90px;
    resize: vertical;
    font-weight: 500;
    text-transform: none;
    letter-spacing: normal;
  }
`;

const StoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.75rem;
`;

const StoreOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem;
  border: 1px solid ${props => props.$checked ? '#a5b4fc' : '#e2e8f0'};
  border-radius: 12px;
  background: ${props => props.$checked ? '#eef2ff' : '#ffffff'};
  cursor: pointer;

  input {
    margin-top: 0.2rem;
  }

  strong {
    display: block;
    color: #0f172a;
  }

  span {
    display: block;
    margin-top: 0.2rem;
    color: #64748b;
    font-size: 0.82rem;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid ${props => props.$primary ? '#4f46e5' : '#e2e8f0'};
  background: ${props => props.$primary ? '#4f46e5' : 'white'};
  color: ${props => props.$primary ? 'white' : '#334155'};
  border-radius: 10px;
  padding: 0.7rem 1rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const PathPreview = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
  padding: 1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  color: #334155;
`;

const Stop = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.65rem;
  border-radius: 999px;
  background: white;
  border: 1px solid #e2e8f0;
  font-weight: 700;
`;

const EmptyState = styled.div`
  color: #94a3b8;
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  text-align: center;
`;

const getStoreAddress = (store) => {
  if (!store?.address) return '';
  if (typeof store.address === 'string') return store.address;
  return [store.address.street, store.address.city, store.address.commune].filter(Boolean).join(', ');
};

const defaultRoutes = () => ['08:00', '10:00', '13:00', '16:00'].map((time) => ({
  id: `route-${time.replace(':', '')}`,
  departureTime: time,
  direction: 'stores_to_central',
  storeIds: [],
  notes: ''
}));

const normalizeRouteForClient = (route, index = 0) => ({
  id: route?.id || `route-${Date.now()}-${index}`,
  departureTime: route?.departureTime || '08:00',
  direction: route?.direction || 'stores_to_central',
  storeIds: (route?.storeIds || []).map(String),
  notes: route?.notes || ''
});

const CourierRouteSettings = () => {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState(defaultRoutes);
  const [centralStore, setCentralStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const storeById = useMemo(
    () => new Map(stores.map((store) => [String(store._id || store.id), store])),
    [stores]
  );

  const fetchRoute = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/stores/courier-route', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const incomingRoutes = response.data?.routes || response.data?.routeConfig?.routes || [];
      setRoutes(incomingRoutes.length ? incomingRoutes.map(normalizeRouteForClient) : defaultRoutes());
      setCentralStore(response.data?.centralStore || null);
      setStores(response.data?.stores || []);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load courier route.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoute();
  }, []);

  const updateRoute = (routeId, updates) => {
    setRoutes((prev) => prev.map((route) => (
      route.id === routeId ? { ...route, ...updates } : route
    )));
  };

  const toggleStore = (routeId, storeId) => {
    setRoutes((prev) => prev.map((route) => {
      if (route.id !== routeId) return route;
      const id = String(storeId);
      const exists = route.storeIds.includes(id);
      return {
        ...route,
        storeIds: exists ? route.storeIds.filter((x) => x !== id) : [...route.storeIds, id]
      };
    }));
  };

  const addRoute = () => {
    setRoutes((prev) => [
      ...prev,
      {
        id: `route-${Date.now()}`,
        departureTime: '08:00',
        direction: 'stores_to_central',
        storeIds: [],
        notes: ''
      }
    ]);
  };

  const removeRoute = (routeId) => {
    setRoutes((prev) => prev.length > 1 ? prev.filter((route) => route.id !== routeId) : prev);
  };

  const getPathStops = (route) => {
    const selectedStores = route.storeIds.map((id) => storeById.get(String(id))).filter(Boolean);
    return route.direction === 'central_to_stores'
      ? [centralStore, ...selectedStores].filter(Boolean)
      : [...selectedStores, centralStore].filter(Boolean);
  };

  const saveRoute = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('/api/stores/courier-route', { routes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const savedRoutes = response.data?.routes || response.data?.routeConfig?.routes || routes;
      setRoutes(savedRoutes.map(normalizeRouteForClient));
      setMessage('Courier routes saved.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to save courier route.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell>
      <Card>
        <Header>
          <div>
            <h3><Truck size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} /> {t('courierRoutes.courierRouteConfiguration')}</h3>
            <p>{t('courierRoutes.createMultipleDailyRoutesFor')}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button type="button" onClick={fetchRoute} disabled={loading || saving}>
              <RefreshCw size={16} /> {t('courierRoutes.refresh')}
            </Button>
            <Button type="button" onClick={addRoute} disabled={loading || saving}>
              <Plus size={16} /> {t('courierRoutes.addRoute')}
            </Button>
            <Button type="button" $primary onClick={saveRoute} disabled={loading || saving || !centralStore}>
              <Save size={16} /> {saving ? t('courierRoutes.saving') : t('courierRoutes.saveRoutes')}
            </Button>
          </div>
        </Header>

        <Body>
          {!centralStore && (
            <EmptyState>{t('courierRoutes.setOneStoreAsThe')}</EmptyState>
          )}

          {routes.map((route, routeIndex) => {
            const pathStops = getPathStops(route);
            return (
              <RouteCard key={route.id}>
                <RouteCardHeader>
                  <h4><Clock size={18} /> {t('courierRoutes.route')} {routeIndex + 1}: {route.departureTime}</h4>
                  <Button type="button" onClick={() => removeRoute(route.id)} disabled={routes.length === 1 || saving}>
                    <Trash2 size={15} /> {t('courierRoutes.remove')}
                  </Button>
                </RouteCardHeader>
                <RouteCardBody>
                  <FormGrid>
                    <Field>
                      {t('courierRoutes.departureTime')}
                      <input
                        type="time"
                        value={route.departureTime}
                        onChange={(e) => updateRoute(route.id, { departureTime: e.target.value })}
                      />
                    </Field>
                    <Field>
                      {t('courierRoutes.direction')}
                      <select
                        value={route.direction}
                        onChange={(e) => updateRoute(route.id, { direction: e.target.value })}
                      >
                        <option value="stores_to_central">{t('courierRoutes.storesToCentral')}</option>
                        <option value="central_to_stores">{t('courierRoutes.centralToStores')}</option>
                      </select>
                    </Field>
                  </FormGrid>

                  <Field>
                    {t('courierRoutes.notesForCourier')}
                    <textarea
                      placeholder={t('courierRoutes.exampleStartWithBabEzzouar')}
                      value={route.notes}
                      onChange={(e) => updateRoute(route.id, { notes: e.target.value })}
                    />
                  </Field>

                  <div>
                    <h4 style={{ margin: '0 0 0.75rem', color: '#0f172a' }}>{t('courierRoutes.storesOnThisRoute')}</h4>
                    {stores.length === 0 ? (
                      <EmptyState>{t('courierRoutes.noRetailStoresFound')}</EmptyState>
                    ) : (
                      <StoreGrid>
                        {stores.map((store) => {
                          const storeId = String(store._id || store.id);
                          const checked = route.storeIds.includes(storeId);
                          return (
                            <StoreOption key={storeId} $checked={checked}>
                              <input type="checkbox" checked={checked} onChange={() => toggleStore(route.id, storeId)} />
                              <span>
                                <strong>{store.name}</strong>
                                <span>{getStoreAddress(store) || store.storeCode || 'No address'}</span>
                              </span>
                            </StoreOption>
                          );
                        })}
                      </StoreGrid>
                    )}
                  </div>

                  <div>
                    <h4 style={{ margin: '0 0 0.75rem', color: '#0f172a' }}>{t('courierRoutes.courierPathPreview')}</h4>
                    <PathPreview>
                      <Stop><Clock size={14} /> {route.departureTime}</Stop>
                      {pathStops.length === 0 ? (
                        <span style={{ color: '#94a3b8' }}>{t('courierRoutes.selectStoresToBuildThe')}</span>
                      ) : (
                        pathStops.map((stop, index) => (
                          <React.Fragment key={`${stop._id || stop.id}-${index}`}>
                            {index > 0 && <ArrowRight size={16} color="#94a3b8" />}
                            <Stop><MapPin size={14} /> {stop.name}</Stop>
                          </React.Fragment>
                        ))
                      )}
                    </PathPreview>
                  </div>
                </RouteCardBody>
              </RouteCard>
            );
          })}

          {message && (
            <div style={{ color: message.includes('saved') ? '#166534' : '#991b1b', display: 'flex', gap: 8, alignItems: 'center' }}>
              {message.includes('saved') && <CheckCircle size={16} />} {message}
            </div>
          )}
        </Body>
      </Card>
    </Shell>
  );
};

export default CourierRouteSettings;
